import cookies from "js-cookie";
import fp from "lodash/fp";
import { reset } from "redux-form";

import { FeatureManager } from "globalConstants";
import { setExpiresDate } from "helpers";
import { fusionAuthService, userService } from "services";
import { withResetStateConst } from "store/hocs";

import { userConstants } from "./user.constants";
import { uiActions } from "../ui/ui.actions";

export const userActions = {
	logout,
	setIsSessionExpired,
	getPermissions,
	login,
	forgotPassword,
	resetPassword,
	verifyCode,
	setUserName,
};

function getPermissions({ fromStore } = {}) {
	return (dispatch, getState) => {
		if (fromStore) {
			// Return permissions if they have already been loaded
			const permissions = getState().user.permissions;
			if (permissions?.length > 0) {
				return Promise.resolve(permissions);
			}
		}
		if (!localStorage.getItem("user")) {
			dispatch(preventLoadPermissions());
			return Promise.resolve();
		}
		dispatch(uiActions.showSpinnerOverlay(false));
		dispatch(request());

		return userService
			.getPermissions()
			.then(result => {
				dispatch(success(result));
				return Promise.resolve(result);
			})
			.catch(error => {
				dispatch(failure(error));
				return Promise.reject(error);
			})
			.finally(() => {
				dispatch(uiActions.hideSpinnerOverlay());
			});
	};

	function request() {
		return {
			type: userConstants.GET_PERMISSIONS_REQUEST,
		};
	}

	function success(permissions) {
		return {
			type: userConstants.GET_PERMISSIONS_SUCCESS,
			permissions,
		};
	}

	function failure(error) {
		return {
			type: userConstants.GET_PERMISSIONS_ERROR,
			error,
		};
	}

	function preventLoadPermissions() {
		return {
			type: userConstants.PREVENT_LOAD_PERMISSIONS,
		};
	}
}

const logoutService = () =>
	FeatureManager.isEnabled("fusionAuth")
		? fusionAuthService.goToLogout()
		: userService.logout();

function logout() {
	return (dispatch, getState) => {
		const reduxFormNames = Object.keys(getState().form);
		fp.map(dispatch, fp.map(reset, reduxFormNames));

		dispatch(resetGlobalState());

		dispatch(uiActions.showSpinnerOverlay());

		setTimeout(() => {
			localStorage.removeItem("user");
			logoutService();
		}, 0);

		dispatch(uiActions.hideSpinnerOverlay());
	};

	function resetGlobalState() {
		return {
			type: withResetStateConst.RESET_GLOBAL_STATE,
		};
	}
}

function setIsSessionExpired(isSessionExpired) {
	return {
		type: userConstants.SET_SESSION_IS_EXPIRED,
		isSessionExpired,
	};
}

function login(username, password, email) {
	return dispatch => {
		dispatch(request());
		if (!cookies.get("_deviceId")) {
			let deviceId = new Date().getTime();
			cookies.set("_deviceId", deviceId, {
				expires: 31,
				path: "/",
			});
		}
		return userService
			.login(username, password, cookies.get("_deviceId"), email)
			.then(
				data => {
					setExpiresDate(data.data);

					dispatch(success(data));
					return Promise.resolve(data);
				},
				error => {
					dispatch(failure(error));
					return Promise.reject(error);
				},
			);
	};
	function request() {
		return {
			type: userConstants.LOGIN_REQUEST,
		};
	}
	function success(response) {
		return {
			type: userConstants.LOGIN_SUCCESS,
			response,
		};
	}
	function failure(error) {
		return {
			type: userConstants.LOGIN_FAILURE,
			error,
		};
	}
}

function forgotPassword(username, email) {
	return dispatch => {
		dispatch(request(null));
		return userService.forgotPassword(username, email).then(
			() => {
				dispatch(success());
				return Promise.resolve();
			},
			error => {
				dispatch(failure(error || "error"));
				return Promise.reject(error);
			},
		);
	};
	function request() {
		return {
			type: userConstants.FORGOT_PASSWORD_REQUEST,
		};
	}
	function success() {
		return {
			type: userConstants.FORGOT_PASSWORD_SUCCESS,
		};
	}
	function failure(error) {
		return {
			type: userConstants.FORGOT_PASSWORD_FAILURE,
			error,
		};
	}
}

function resetPassword(userId, code, password) {
	return dispatch => {
		dispatch(request(null));
		return userService.resetPassword(userId, code, password).then(
			() => {
				dispatch(success());
				return Promise.resolve();
			},
			error => {
				dispatch(failure(error || "error"));
				return Promise.reject(error);
			},
		);
	};
	function request() {
		return {
			type: userConstants.RESET_PASSWORD_REQUEST,
		};
	}
	function success() {
		return {
			type: userConstants.RESET_PASSWORD_SUCCESS,
		};
	}
	function failure(error) {
		return {
			type: userConstants.RESET_PASSWORD_FAILURE,
			error,
		};
	}
}

function verifyCode(userId, code) {
	return dispatch => {
		dispatch(request(null));

		return userService.verifyCode(userId, code).then(
			() => {
				dispatch(success());
				return Promise.resolve();
			},
			error => {
				dispatch(failure(error || "error"));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return {
			type: userConstants.VERIFY_CODE_REQUEST,
		};
	}

	function success() {
		return {
			type: userConstants.VERIFY_CODE_SUCCESS,
		};
	}

	function failure(error) {
		return {
			type: userConstants.VERIFY_CODE_FAILURE,
			error,
		};
	}
}

function setUserName({ firstName, lastName }) {
	return {
		type: userConstants.SET_USER_PROFILE_NAME,
		firstName,
		lastName,
	};
}
